/**
 * AICoachProgressTabs Component
 *
 * Displays the progress tabs for the AI Coach intro and 3 analysis parts.
 *
 * Responsive:
 * - Mobile: Horizontally scrollable, smaller text
 * - Desktop: Centered, full-size text
 */

import React, { useMemo } from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { STEP_TITLE_KEYS } from '../constants'
import { AI_COACH_PARTS, type AICoachPart } from '../../../../../../../pure-js/types/AICoach'

// Subtle pulse animation for current step
const subtlePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`

type StepStatus = 'completed' | 'current' | 'upcoming'

interface Step {
  step: AICoachPart
  title: string
  status: StepStatus
}

interface AICoachProgressTabsProps {
  currentPart: AICoachPart | null
}

/**
 * Get the color for a step based on its status
 */
const getStepColor = (status: StepStatus): string => {
  switch (status) {
    case 'completed':
      return INTERAX_COLORS.green
    case 'current':
      return INTERAX_COLORS.sun
    case 'upcoming':
    default:
      return INTERAX_COLORS.white40
  }
}

export const AICoachProgressTabs: React.FC<AICoachProgressTabsProps> = ({ currentPart }) => {
  const { t } = useTranslation()

  // Build steps array for progress tabs
  const steps = useMemo<Step[]>(() => {
    return AI_COACH_PARTS.map((step) => ({
      step,
      title: t(STEP_TITLE_KEYS[step]),
      status:
        currentPart === null
          ? 'completed'
          : step < currentPart
            ? 'completed'
            : step === currentPart
              ? 'current'
              : 'upcoming'
    }))
  }, [currentPart, t])

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: { xs: '16px', sm: '20px', md: '24px', lg: '24px' },
        // Allow horizontal scroll on mobile
        overflowX: { xs: 'auto', sm: 'visible' },
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          // Ensure tabs don't wrap on mobile
          flexShrink: 0
        }}
      >
        {steps.map((step, i) => (
          <React.Fragment key={step.step}>
            <Box
              sx={{
                position: 'relative',
                px: { xs: '12px', sm: '16px', md: '20px', lg: '20px' },
                py: { xs: '8px', sm: '12px', md: '12px', lg: '12px' },
                cursor: 'default',
                whiteSpace: 'nowrap'
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16, md: 16, lg: 16 },
                  fontWeight: step.status === 'current' ? 600 : 400,
                  color: getStepColor(step.status),
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: '8px', sm: '12px' },
                  ...(step.status === 'current' && { animation: `${subtlePulse} 2s ease-in-out infinite` })
                }}
              >
                {step.status === 'completed' && (
                  <CheckCircleIcon
                    style={{
                      width: 16,
                      height: 16,
                      flexShrink: 0
                    }}
                  />
                )}
                {step.title}
              </Typography>
              {step.status === 'current' && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: { xs: 12, sm: 16, md: 20, lg: 20 },
                    right: { xs: 12, sm: 16, md: 20, lg: 20 },
                    height: 2,
                    backgroundColor: INTERAX_COLORS.sun,
                    borderRadius: 4
                  }}
                />
              )}
            </Box>
            {i < steps.length - 1 && (
              <Typography
                sx={{
                  color: INTERAX_COLORS.white20,
                  mx: { xs: '8px', sm: '12px', md: '16px', lg: '16px' },
                  fontSize: { xs: 12, sm: 14 }
                }}
              >
                |
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}
